/**
 * artigos.ts — Conteúdo da Central de Ajuda (markdown) + índice de seções.
 *
 * Cada artigo é um markdown curto, em linguagem de tarefa (passo a passo + Dica).
 * `ARTIGOS` guarda os que já têm conteúdo; `SECOES` é o índice mostrado na página
 * (um item sem artigo correspondente aparece como "em breve").
 */

export interface Artigo {
  slug: string;
  secao: string;
  titulo: string;
  md: string;
}

export interface ItemIndice {
  titulo: string;
  slug: string;
}

export interface Secao {
  icone: string;
  nome: string;
  artigos: ItemIndice[];
}

// ─── Artigos com conteúdo ────────────────────────────────────────────────────

export const ARTIGOS: Artigo[] = [
  {
    slug: 'inbox-o-que-e',
    secao: 'Inbox',
    titulo: 'O que é a Inbox (e o que aparece nela)',
    md: `A **Inbox** é onde chega o que é dirigido a **você** — recado, pendência
e decisão, tudo na mesma tela. Ela abre pelo menu, em **Início › Inbox**.

Duas coisas aparecem ali:

- **Avisos** — alguém te mencionou no chat de uma OS, a integração com o Tiny
  caiu, uma despesa ficou sem comprovante.
- **Pedidos de autorização** — os que envolvem você, esperando decisão ou já
  decididos.

## Como ler uma linha

Da esquerda para a direita: a **caixa de seleção**, a **estrela**, o **rosto de
quem mandou** (com um selo do que é aquilo) e o assunto. À direita, **quando
chegou** e há quanto tempo está parado.

A **faixa colorida na borda esquerda** diz o que se espera de você:

- **laranja** — está esperando a sua decisão;
- **amarelo** — é uma pendência, algo para resolver;
- **verde** — já foi resolvido ou arquivado;
- **cinza** — é só para você saber.

> As linhas com **fundo mais escuro** são as que você ainda não leu. O nome de
> quem mandou também fica em negrito, e o envelope à direita fica fechado.`,
  },
  {
    slug: 'inbox-pastas',
    secao: 'Inbox',
    titulo: 'As pastas e os filtros',
    md: `A Inbox tem **duas formas de filtrar ao mesmo tempo**, e elas se
combinam.

## As pastas (coluna da esquerda)

Respondem **o que se espera de você**:

- **Tudo** — avisos e pedidos juntos, do mais novo para o mais velho.
- **Para resolver** — o que exige uma ação sua, incluindo os pedidos de
  autorização que esperam decisão.
- **Só para saber** — o que morre quando você lê.
- **Favoritos** — o que você marcou com a estrela.
- **Arquivados** — o que já saiu da lista. **Nada é apagado**: arquivar é tirar
  da frente, e o que foi arquivado continua aqui.

## A faixa (acima da lista)

Responde **que tipo de coisa**: **Tudo · Avisos · Autorizações**.

Os dois se cruzam. "Para resolver" + "Autorizações" mostra só os pedidos parados
esperando você.

> **No celular** as pastas viram um botão largo no alto, com o nome da pasta e
> quantos itens ela tem. Tocar nele abre a lista completa pela lateral.`,
  },
  {
    slug: 'inbox-acoes',
    secao: 'Inbox',
    titulo: 'Estrela, lido e arquivar',
    md: `Cada linha tem três ações, e nenhuma delas apaga nada.

## A estrela — marcar como favorito

Serve para **achar de novo depois**. O que está marcado aparece na pasta
**Favoritos**, e o arquivamento em lote **não leva** um favorito junto.

A linha **não muda de lugar** quando você marca — a ordem é sempre a data.

## O envelope — lido e não lido

Fechado quer dizer **por ler**; aberto, **lido**. Clicar troca nos dois
sentidos: dá para **voltar** um item para não-lido quando você abriu sem tempo
de tratar dele.

Abrir o item também o marca como lido. No **⋮** da barra existe "Marcar tudo
como lido".

## Arquivar — tirar da frente

O item sai da lista e vai para a pasta **Arquivados**. Nada é apagado.

Para arquivar vários de uma vez: marque as caixas de seleção e use o ícone de
**arquivo** na barra de cima. Ele pede confirmação, e duas coisas ele nunca
leva:

1. **decisão em aberto** — alguém está esperando do outro lado;
2. **o que você marcou como favorito**.

> Depois do lote, a tela diz quantos foram e quantos ficaram. Se o número não
> bater, é por um dos dois motivos acima.`,
  },
  {
    slug: 'perfis',
    secao: 'Começando',
    titulo: 'O sistema e os perfis (quem vê o quê)',
    md: `Este é o sistema de operações da **Casa A** — Búzios e Trancoso na mesma
ferramenta. Cada pessoa entra com a **própria conta**, e o que aparece para você
depende do seu **perfil**.

## Os perfis

- **Administrador** — vê e faz tudo.
- **Gestor** — acompanha a operação toda (OS, agenda, viagens, despesas).
- **Vendedor** — foca nas próprias OS e no que vendeu.
- **Instalador** — o dia a dia da rua; na prática usa muito os **formulários públicos** (lançar despesa), sem precisar entrar no sistema.

## O menu segue o seu perfil

O menu à esquerda é dividido em **Operação**, **Financeiro** e **Comercial**.
O seu perfil decide **quais itens aparecem** — se você não enxerga uma área, é
porque o seu perfil não a inclui, não é erro.

> **Dica:** quem ajusta os perfis e o que cada um acessa é o **dono** do sistema, na área **Perfis & Acessos**. Se você precisa ver algo que não aparece, fale com ele.`,
  },
  {
    slug: 'login-conta',
    secao: 'Começando',
    titulo: 'Entrar, sair, foto e senha',
    md: `Sua conta é pessoal. Você entra com **usuário ou e-mail** e a sua **senha**.

## Entrar e sair

1. Na tela de acesso, digite o **usuário ou e-mail** e a **senha**. O ícone de **olho** mostra a senha para conferir.
2. Clique em **Entrar**. A sessão dura bastante tempo — no dia a dia você não precisa entrar toda hora.
3. Para **sair**, clique no seu **avatar** (canto superior direito) e em **Sair** — ou no botão de sair no rodapé do menu.

## Trocar foto, nome e senha

1. Clique no seu **avatar** e em **Meu perfil**.
2. Ali você troca a **foto**, ajusta **nome** e **e-mail** e clica em **Salvar dados**.
3. No cartão **Trocar senha**, informe a **senha atual** e a **nova** (mínimo de 6 caracteres) e confirme.

No mesmo menu do avatar há **Preferências**, onde você escolhe a **cor** do tema e o modo **claro/escuro** (fica salvo neste navegador).

> **Dica:** esqueceu a senha? Não há recuperação automática — peça ao administrador para gerar uma nova. **Usuário e perfil** só o administrador altera; o resto é você quem cuida.`,
  },
  {
    slug: 'agendar-visita',
    secao: 'Agenda',
    titulo: 'Agendar uma visita',
    md: `A **visita** é o compromisso do instalador num dia — instalação, medição ou
manutenção. Ela sempre nasce de uma **Ordem de Serviço (OS)**.

## Como agendar

1. Abra a **Agenda**. No painel **"OS a agendar"** ficam as OS que ainda precisam de dia.
2. **Arraste** o cartão da OS (pela alça) até o dia desejado no calendário — ou clique no cartão e depois no dia.
3. No diálogo, confira a **data**. Para uma instalação de vários dias, informe **data de início e fim**: o sistema cria uma visita por dia.
4. Se o cliente marcou horário, preencha a **hora** (é opcional).
5. Escolha **quem vai** (instaladores) e salve.

## Editar, excluir ou reagendar

Clique na visita no calendário: abre uma janela com o resumo, dividida em abas
(**Visita**, **Produtos**, **Levar & NF**, **Idas**). Dali você **escreve o que fazer**
naquela ida, **edita** a visita (data, hora, quem vai), **exclui** (botão no canto
esquerdo de baixo) ou **abre a OS**. No próprio cartão do calendário também há o
**lápis** (editar) e a **lixeira** (excluir). Para reagendar rápido, **arraste** a
visita para outro dia.

## Quando o dia fica cheio

Se o dia tem mais de 5 compromissos, aparece **"+2 ⌄"** no fim do dia. Clique: o
**calendário cresce** e mostra tudo daquele dia. Para voltar ao tamanho normal, use
o **"⌃ recolher"** no fim do dia ou o botão **Recolher dias** na barra de cima.

## As marcações no alto do dia

O sistema mostra sozinho, ao lado do número do dia:

- **Livre** — nenhum compromisso marcado.
- **Livre de instalações** — tem compromisso (entrega, reunião, lembrete), mas nenhuma
  instalação. É o dia onde ainda cabe encaixar trabalho.
- a **pílula do destino** (ex.: "Rio de Janeiro") quando há viagem no dia.

Visita **cancelada não ocupa** o dia: se a instalação caiu, o dia volta a aparecer como
livre de instalações.

> **Dica:** hora é exceção. Só preencha quando o cliente impôs um horário ("depois das 15h"). Sem hora, a visita aparece no dia sem relógio — como a maioria funciona.`,
  },
  {
    slug: 'evento-avulso',
    secao: 'Agenda',
    titulo: 'Evento avulso (entrega, retirada, lembrete)',
    md: `Nem tudo na agenda é visita de OS. **Pegar material no fornecedor, entregar
mercadoria, uma folga do instalador, um lembrete** — tudo isso são **eventos
avulsos**, que não dependem de uma OS.

## Como criar

1. Na Agenda, clique no **+** no topo do dia.
2. Escolha o **tipo**: Entrega, Retirada, Serviço na loja, Ausência, Reunião, **Dia sem OS** ou Lembrete.
3. Dê um **título** (ex.: "Pegar motor na Nitflex") e, se quiser, uma observação e hora.
4. Preencha o **endereço** na aba **Endereço** — o evento avulso tem endereço próprio
   (rua, número, bairro, cidade). É ele que manda o evento para o **bairro certo do
   roteiro impresso**; sem endereço, sai como "Sem endereço informado".
5. Salve.

O evento avulso aparece no dia com **visual diferente** da OS, para você distinguir de relance.

## Os dois tipos que confundem

- **Ausência** é **pessoa** indisponível: folga do instalador, médico, férias.
- **Dia sem OS** é uma anotação **sobre o dia**: "aqui não houve instalação" — por
  exemplo, a semana em que a equipe foi ao Rio e as instalações não aconteceram. Ele
  aparece **riscadinho**, para não parecer tarefa a fazer.

> **Dica:** se ainda não existe OS (ex.: pegar material de um pedido que só vai instalar daqui a muito tempo), use o evento avulso e escreva o cliente/pedido no texto. Depois, quando a OS existir, você agenda a visita normal.`,
  },
  {
    slug: 'ligar-viagem',
    secao: 'Agenda',
    titulo: 'Ligar o dia a uma viagem',
    md: `Quando os instaladores viajam (Rio, Bahia…), o dia vira uma **Viagem**. Ligar
os eventos do dia à viagem serve para controle e para montar o roteiro.

## Como ligar

1. No card do evento (visita ou avulso), há um **chip de viagem**.
2. Clique nele: se há **uma** viagem no dia, o evento entra nela na hora.
3. Se há **mais de uma** viagem no dia (ex.: Búzios e Trancoso), abre um menu — **escolha a viagem**.
4. Para tirar o evento da viagem, clique no chip de novo.

O **cabeçalho do dia** mostra uma pílula com o destino, indicando que há viagem naquele dia.

## Arrastar para fora tira da viagem

Se você **arrasta** um evento para um dia **fora do período da viagem**, ele sai dela
automaticamente — aparece o aviso "Saiu da viagem X" com o botão **Desfazer** (que
religa mantendo a data nova). Dentro do período, o vínculo continua. Assim o evento
remarcado **não volta a aparecer** na impressão daquela viagem.

> **Dica:** a mesma OS pode aparecer em duas viagens por eventos diferentes — a **entrega** numa viagem e a **instalação** noutra. Cada evento diz o que foi feito naquele dia.`,
  },
  {
    slug: 'imprimir-roteiro',
    secao: 'Agenda',
    titulo: 'Imprimir o roteiro (do dia e da viagem toda)',
    md: `O **roteiro** é a folha que os instaladores levam para a rua. O sistema monta
sozinho a partir do que você agendou — não precisa mais digitar no Word.

## Roteiro do dia

1. Na Agenda, no topo do dia, clique no ícone de **impressora**.
2. Abre a folha pronta: as paradas na ordem, **agrupadas por destino**, com cliente, ação, pedido, OS, **notas fiscais** e **o que levar**.
3. Clique em **Imprimir / Salvar PDF**, ou em **Copiar imagem** para colar direto no WhatsApp.

## Roteiro da viagem toda

Se o dia tem viagem, clique na **pílula da viagem** (em vez da impressora) e escolha **"Viagem toda"** — a folha sai com **todos os dias da viagem** de uma vez.

> **Dica:** o que aparece em "notas fiscais" e "o que levar" vem da aba **Roteiro** dentro da OS. Preencha lá e o roteiro do dia puxa automático.`,
  },

  {
    slug: 'abrir-os',
    secao: 'Ordens de Serviço',
    titulo: 'Abrir uma OS (com ou sem pedido)',
    md: `A **OS (Ordem de Serviço)** é o trabalho num endereço — uma instalação,
manutenção ou lavagem. Cada OS tem um código por loja e ano (ex.: **BZ-2026-0001**).

## Como abrir

1. Em **Ordens de Serviço**, clique em **Nova OS**.
2. Escolha a **loja** (Búzios ou Trancoso) e o **tipo** (instalação, manutenção ou lavagem).
3. Se a venda tem **pedido no Tiny**, informe o número e use **Puxar do ERP** — o sistema traz cliente, endereço e itens prontos.
4. Se **não há pedido** (uma manutenção, uma visita técnica), marque **sem pedido** e preencha à mão.
5. Salve. A OS já pode ser agendada, receber fotos e seguir o fluxo.

## As abas da OS

O trabalho fica organizado em abas: **Dados & Cliente**, **Produtos**, **Ambientes**, **Visitas**, **Roteiro**, **Execução**, **Chat** e mais. Você preenche o que precisar, quando precisar.

> **Dica:** OS sem pedido é normal — nem todo serviço nasce de uma venda no Tiny (manutenção, garantia, visita técnica). Não force um pedido que não existe.`,
  },
  {
    slug: 'puxar-tiny',
    secao: 'Ordens de Serviço',
    titulo: 'Puxar do Tiny (ERP)',
    md: `Quando a venda foi feita no **Tiny (ERP)**, você não precisa redigitar nada:
o sistema puxa os dados do pedido direto de lá.

## Como puxar

1. Na aba **Dados & Cliente**, informe o **número do pedido**.
2. Clique em **Puxar do ERP**.
3. O sistema traz **cliente, endereço, itens e observações** do pedido.
4. Confira e ajuste o que precisar.

## Trava de segurança

Se a OS já tem dados preenchidos, o **Puxar** pede **confirmação** antes de sobrescrever — para você não perder o que já digitou por engano.

> **Dica:** puxe o pedido logo no começo, com a OS ainda vazia. Assim você parte dos dados oficiais do Tiny e só complementa (medidas, fotos, observações).`,
  },
  {
    slug: 'os-cliente',
    secao: 'Ordens de Serviço',
    titulo: 'Cliente, endereço e contato',
    md: `Os dados do cliente e do endereço ficam na aba **Dados & Cliente**. Se você
puxou do Tiny, já vêm preenchidos; senão, preencha à mão.

## O que preencher

- **Cliente:** nome, **telefone** e **WhatsApp** (com bandeira do país) e e-mail.
- **Endereço:** rua, número, bairro, cidade, CEP e um **ponto de referência** (ajuda o instalador a achar).
- **Vendedor responsável:** quem vendeu (um usuário do sistema ou texto livre) e, se houver, o **arquiteto**.
- **Anotações do cliente:** observações livres (ex.: "portão azul, tocar 2x"), com a opção de **incluir no PDF** da OS.

> **Dica:** o telefone e o WhatsApp saem formatados no PDF, com ícone — o instalador liga ou chama direto. O CPF nunca aparece no PDF, por segurança.`,
  },
  {
    slug: 'os-medidas',
    secao: 'Ordens de Serviço',
    titulo: 'Medidas e fotos por medida',
    md: `As **medidas** e as **fotos** ficam na aba **Ambientes**. Cada ambiente é um
cômodo; dentro dele, cada **abertura** (janela, porta) tem sua medida — e **as fotos
ficam por medida**, não soltas.

## Como preencher

1. Na aba **Ambientes**, adicione um **cômodo** (ex.: "Sala", "Quarto casal").
2. Dentro do cômodo, adicione as **medidas** por abertura: largura, altura, produto, fixação.
3. Em cada medida, anexe as **fotos daquela abertura** — assim o instalador vê exatamente onde cada produto vai.

## Fotos

As fotos são **comprimidas automaticamente** ao enviar (elas vão para o PDF e costumam ser tiradas no celular). Você pode tirar na hora ou subir do computador.

> **Dica:** foto por medida vale ouro na rua. O instalador bate o olho e sabe qual cortina é de qual janela, sem ligar para a loja.`,
  },
  {
    slug: 'os-execucao',
    secao: 'Ordens de Serviço',
    titulo: 'Execução do instalador',
    md: `A aba **Execução** é onde o **instalador registra o que fez** na rua — em texto
e fotos. Cada lançamento fica guardado (não sobrescreve o anterior).

## Como usar

1. Na OS, abra a aba **Execução**.
2. Escreva o que foi feito e anexe **fotos** do serviço pronto.
3. Cada registro entra na lista, com autor e data.

## Trava

Um **admin ou gestor** pode **travar** a execução de uma OS. Com ela travada, os instaladores passam a ver a execução em **somente leitura** — útil quando o trabalho já foi conferido e fechado.

> **Dica:** registre logo após terminar, ainda no local. As fotos de execução também são comprimidas no envio.`,
  },
  {
    slug: 'os-imprimir',
    secao: 'Ordens de Serviço',
    titulo: 'Imprimir a OS',
    md: `A **OS impressa** é o detalhe completo que o instalador leva — cliente,
endereço, produtos, ambientes com medidas e fotos. É diferente do **roteiro do dia**
(que é a prévia leve de várias OS).

## Como imprimir

1. Abra a OS e clique em **Imprimir**.
2. Abre uma folha limpa, pronta para impressão.
3. Alterne entre **retrato e paisagem** conforme couber melhor.
4. Clique em **Imprimir / Salvar PDF**.

Aparece: dados do cliente e endereço, vendedor, pedido, **produtos** e os **ambientes** (cada cômodo com suas medidas e fotos). Só sai o que estiver preenchido.

> **Dica:** para a folha do dia com várias paradas, use o **roteiro** na Agenda. A OS impressa é para o detalhe de **uma** OS.`,
  },

  {
    slug: 'viagem-criar',
    secao: 'Viagens',
    titulo: 'Criar e encerrar uma viagem',
    md: `A **viagem** junta um deslocamento e tudo que ele custou. Serve para saber
quanto se gastou, em quê, e quem pagou.

## Criar

1. Em **Viagens**, clique em **Nova viagem**.
2. Escolha o **tipo**: **Operacional** (instaladores executando OS — é a única que agrega OS e alimenta o custo do pedido), **Sócios** ou **Administrativa** (deslocamentos sem OS).
3. Preencha o **roteiro**: **origem**, a **loja** (empresa) dona da viagem — Búzios já vem marcada —, os **destinos em ordem**, as **datas** de partida e chegada e o **transporte**.
4. Marque **quem viaja** (na operacional, os instaladores).
5. Salve. A viagem começa como **Planejada**; ajuste o **status** conforme ela anda.

## Encerrar

Na tela da viagem, clique em **Encerrar viagem**. Isso **fecha as contas**: o total gasto vira o custo definitivo. Uma despesa que chegar depois disso exige **Reabrir** a viagem antes de lançar.

> **Dica:** as **OS não se escolhem aqui** — elas entram pela Agenda. Veja *"Incluir uma OS na viagem"*. A **data de chegada já é a volta**: os instaladores saem de Búzios e voltam para Búzios.`,
  },
  {
    slug: 'viagem-os',
    secao: 'Viagens',
    titulo: 'Incluir uma OS na viagem',
    md: `Numa viagem **operacional**, as OS entram pela **Agenda**, não pelo cadastro
da viagem. Assim cada dia diz o que foi feito, e o gasto encontra a OS certa.

## Como incluir

1. Na **Agenda**, abra o dia da viagem.
2. No card do evento (visita ou avulso), clique no **chip de viagem** para ligá-lo à viagem — se houver mais de uma no dia, escolha qual.
3. Pronto: a OS daquele evento passa a fazer parte da viagem.

## O que aparece na viagem

Na tela da viagem, o bloco **OS da viagem** lista as OS incluídas, cada uma com o **gasto direto** (as despesas que apontam aquela OS). As despesas **sem OS específica** ficam separadas como **"a ratear"** entre as OS — dividir esse valor é um passo futuro; aqui o sistema não inventa número.

> **Dica:** para **tirar** uma OS da viagem, volte ao evento na Agenda e clique no chip de viagem de novo. Veja também *"Ligar o dia a uma viagem"* na seção Agenda.`,
  },

  {
    slug: 'despesa-lancar',
    secao: 'Despesas',
    titulo: 'Lançar uma despesa',
    md: `Pela tela de **Despesas** (interna, com login) o financeiro registra e
acompanha os gastos — com **loja**, **categoria** e **forma de pagamento**, do
jeito que espelha o Tiny.

## Lançar

1. Em **Despesas**, clique em **Nova despesa** (abre uma página própria, com espaço para tudo).
2. Informe **valor** e **data**, escolha a **categoria** e a **forma de pagamento**.
3. Em **Vínculos**, escolha a **loja**; se a despesa é de uma viagem, escolha a **viagem** (ela filtra as **OS** e já ajusta a loja) e, se for de uma OS específica, a **OS**.
4. Anexe o **comprovante** (foto ou arquivo) e salve.

## Restituição e acompanhamento

Quando a forma é **Dinheiro próprio**, a despesa entra para **restituição** de quem pagou. Na lista de Despesas você **filtra** (loja, categoria, período), vê o **total** e marca uma despesa como **restituída**. Clique numa linha para **editar**.

> **Dica:** o instalador na rua não usa esta tela — ele lança pelo **formulário público** (veja o próximo artigo), e o lançamento dele cai aqui com a origem **Campo**.`,
  },
  {
    slug: 'despesa-publico',
    secao: 'Despesas',
    titulo: 'Formulário público do instalador',
    md: `O instalador lança despesa do **celular, sem login**, por um link fixo. É a
forma de o gasto da rua entrar no sistema na hora.

## Como usar

1. Abra o link do formulário (a Casa A compartilha com os instaladores).
2. Escolha **quem está lançando** — é obrigatório.
3. Informe **valor** e **data** e toque em **como você pagou** (Banco Inter, Caixa ou Dinheiro próprio).
4. Se quiser, escolha a **categoria** e escreva uma **descrição**.
5. Confira a **loja** (Búzios já vem marcada). Se estiver **numa viagem**, selecione-a — aí dá para apontar a **OS** (opcional).
6. Tire a **foto do comprovante** e toque em **Enviar despesa**.

Ao enviar, aparece a confirmação e o botão **Lançar outra** (mantém o instalador selecionado, para lançar em sequência).

> **Dica:** pagou com **Dinheiro próprio**? O formulário avisa que a despesa vai para **restituição** a você. A localização é capturada em silêncio (se o celular permitir), para ajudar a conferir depois.`,
  },

  {
    slug: 'autorizacao-pedir',
    secao: 'Autorizações',
    titulo: 'Pedir uma autorização',
    md: `Precisa do **aval da Adriana** para um desconto, uma compra, uma cortesia?
Registre a autorização no sistema — assim fica **consultável** depois, e ninguém
precisa lembrar de cabeça.

## Como pedir

1. Em **Autorizações**, clique em **Nova autorização**.
2. Escolha o **tipo** (Desconto, Material extra, Compra, Cortesia… ou deixe **Genérica**).
3. Dê um **título** claro (ex.: *"Desconto de 20% no pedido do cliente X"*) — é o único campo obrigatório.
4. Se fizer sentido, informe **valor (R$)** e/ou **percentual (%)** e escreva a **justificativa**.
5. Em **Quem e onde**, indique o **solicitante** (em nome de quem é o pedido, se não for você) e **vincule a uma OS**, se houver.
6. Clique em **Registrar pedido**. A **Adriana é notificada** na hora.

> **Dica:** o pedido vira uma **conversa**: dá para trocar mensagens (e mandar **imagem**) até a decisão sair. Registre em vez de combinar só no WhatsApp — aqui fica o histórico.`,
  },
  {
    slug: 'autorizacao-aprovar',
    secao: 'Autorizações',
    titulo: 'Aprovar (Adriana)',
    md: `Toda autorização passa pela **Adriana**. Ela abre o pedido, conversa se
precisar, e decide — e o motivo fica guardado.

## Decidir

1. Abra a autorização (pela notificação ou pela lista de **Autorizações**).
2. No cartão **Decidir**, escreva, se quiser, uma **observação visível a todos**.
3. Use a **anotação privada** para registrar *por que* você decidiu — ela fica visível **só para você e o dono**, para lembrar meses depois.
4. Clique em **Autorizar** ou **Negar**.

## Conversar, negar e reabrir

Antes de decidir, use a **conversa** para tirar dúvidas com quem pediu (aceita **imagem**). Uma autorização **negada** pode ser **reaberta** — só o aprovador faz isso. **Cancelada** encerra de vez.

> **Dica:** a anotação privada é a sua memória. "Autorizei porque o cliente é antigo e fechou três pedidos" vale ouro quando alguém perguntar o porquê lá na frente.`,
  },
  {
    slug: 'autorizacao-acompanhamento',
    secao: 'Autorizações',
    titulo: 'Acompanhamento: etapas, registros e documentos',
    md: `Autorização que **não se resolve num sim ou num não** vira trabalho: falar
com o fornecedor, esperar a nota, conferir o pagamento. O **Acompanhamento** é
onde esse trabalho fica visível — em vez de viver na cabeça de quem começou.

## As etapas

Cada etapa é **uma frase, um responsável e (às vezes) um prazo**. Elas podem
depender umas das outras: enquanto a anterior não termina, a seguinte espera.

Cada etapa carrega **dois selos**, e eles respondem perguntas diferentes:

| Selo | O que diz |
|---|---|
| **Não iniciada · Em andamento · Concluída · Dispensada** | em que ponto a etapa está — é o que se escolhe |
| **Bloqueada · Liberada** | se ela já pode ser feita — **ninguém escolhe**: sai das dependências |

**Assumir** é o gesto que põe a etapa em andamento, e é **do responsável**. Para
assumir a etapa de outra pessoa, troque o responsável antes — a troca fica
registrada. Etapa sem responsável nenhum: quem assume fica com ela.

## Registrar o que aconteceu

A aba **Atividades** é a série do que foi feito: *"liguei, não atenderam,
retorno na terça"*. É o que responde, semanas depois, por que o caso demorou.

Adiar o prazo **sempre pede o motivo** — três adiamentos sem explicação não
dizem nada; com explicação, dizem tudo.

## A prova

A aba **Documentos** guarda o que sustenta o relato: a nota, o print da
conversa, o e-mail do fornecedor. Foto ou PDF, até 10 MB cada, vários de uma
vez. Um documento já citado num registro não pode mais ser apagado.

## E o chat

Cada etapa tem o seu, e ele serve para **combinar**, não para registrar: o que
precisa ser lembrado depois vai em Atividades. Use **@** para chamar alguém;
**Enter** envia e **Shift+Enter** quebra a linha.

> **Dica:** prazo vencido acende em qualquer tela onde a etapa apareça, em
> qualquer situação que ela esteja. É o único aviso que atravessa os dois selos.`,
  },
];

// ─── Índice (todas as seções; itens sem artigo = "em breve") ─────────────────

export const SECOES: Secao[] = [
  {
    icone: 'bi-rocket-takeoff',
    nome: 'Começando',
    artigos: [
      { titulo: 'O sistema e os perfis (quem vê o quê)', slug: 'perfis' },
      { titulo: 'Entrar, sair, foto e senha', slug: 'login-conta' },
    ],
  },
  {
    icone: 'bi-inbox',
    nome: 'Inbox',
    artigos: [
      { titulo: 'O que é a Inbox (e o que aparece nela)', slug: 'inbox-o-que-e' },
      { titulo: 'As pastas e os filtros', slug: 'inbox-pastas' },
      { titulo: 'Estrela, lido e arquivar', slug: 'inbox-acoes' },
    ],
  },
  {
    icone: 'bi-clipboard-check',
    nome: 'Ordens de Serviço',
    artigos: [
      { titulo: 'Abrir uma OS (com ou sem pedido)', slug: 'abrir-os' },
      { titulo: 'Puxar do Tiny (ERP)', slug: 'puxar-tiny' },
      { titulo: 'Cliente, endereço e contato', slug: 'os-cliente' },
      { titulo: 'Medidas e fotos por medida', slug: 'os-medidas' },
      { titulo: 'Execução do instalador', slug: 'os-execucao' },
      { titulo: 'Imprimir a OS', slug: 'os-imprimir' },
    ],
  },
  {
    icone: 'bi-calendar-week',
    nome: 'Agenda',
    artigos: [
      { titulo: 'Agendar uma visita', slug: 'agendar-visita' },
      { titulo: 'Evento avulso (entrega, retirada, lembrete)', slug: 'evento-avulso' },
      { titulo: 'Ligar o dia a uma viagem', slug: 'ligar-viagem' },
      { titulo: 'Imprimir o roteiro (do dia e da viagem toda)', slug: 'imprimir-roteiro' },
    ],
  },
  {
    icone: 'bi-truck',
    nome: 'Viagens',
    artigos: [
      { titulo: 'Criar e encerrar uma viagem', slug: 'viagem-criar' },
      { titulo: 'Incluir uma OS na viagem', slug: 'viagem-os' },
    ],
  },
  {
    icone: 'bi-cash-coin',
    nome: 'Despesas',
    artigos: [
      { titulo: 'Lançar uma despesa', slug: 'despesa-lancar' },
      { titulo: 'Formulário público do instalador', slug: 'despesa-publico' },
    ],
  },
  {
    icone: 'bi-shield-check',
    nome: 'Autorizações',
    artigos: [
      { titulo: 'Pedir uma autorização', slug: 'autorizacao-pedir' },
      { titulo: 'Aprovar (Adriana)', slug: 'autorizacao-aprovar' },
      { titulo: 'Acompanhamento: etapas, registros e documentos', slug: 'autorizacao-acompanhamento' },
    ],
  },
];

export const getArtigo = (slug: string): Artigo | undefined =>
  ARTIGOS.find((a) => a.slug === slug);

export const temArtigo = (slug: string): boolean =>
  ARTIGOS.some((a) => a.slug === slug);
